// components/ChangePasswordForm.jsx
import { useState, useEffect } from "react";
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const ChangePasswordForm = ({ email }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    repeatPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    repeat: false,
  });
  const [changeStatus, setChangeStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Calculate password strength
  useEffect(() => {
    if (!formData.newPassword) return setPasswordStrength(0);

    let strength = 0;
    if (formData.newPassword.length >= 8) strength += 1;
    if (/[A-Z]/.test(formData.newPassword)) strength += 1;
    if (/[0-9]/.test(formData.newPassword)) strength += 1;
    if (/[^A-Za-z0-9]/.test(formData.newPassword)) strength += 1;

    setPasswordStrength(strength);
  }, [formData.newPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setChangeStatus(null);

    // Check password match
    if (formData.newPassword !== formData.repeatPassword) {
      setChangeStatus({
        type: "error",
        message: "New passwords do not match.",
      });
      setIsLoading(false);
      return;
    }

    // Check password strength
    if (passwordStrength < 3) {
      setChangeStatus({
        type: "warning",
        message:
          "Your password is weak. Consider using a mix of letters, numbers, and symbols.",
      });
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            email,
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");

      setChangeStatus({ type: "success", message: data.message });
      setFormData({ currentPassword: "", newPassword: "", repeatPassword: "" });
    } catch (err) {
      setChangeStatus({ type: "error", message: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength indicators
  const strengthColors = [
    "bg-red-500",
    "bg-orange-500",
    "bg-yellow-500",
    "bg-green-500",
    "bg-green-600",
  ];

  const strengthLabels = [
    "Very Weak",
    "Weak",
    "Medium",
    "Strong",
    "Very Strong",
  ];

  return (
    <motion.form
      onSubmit={handlePasswordChange}
      className="space-y-6 max-w-md mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Current Password */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Current Password
        </label>
        <div className="relative">
          <input
            name="currentPassword"
            type={showPasswords.current ? "text" : "password"}
            value={formData.currentPassword}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-300 shadow-sm hover:shadow-md focus:shadow-lg"
          />
          <motion.button
            type="button"
            onClick={() => togglePassword("current")}
            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            whileTap={{ scale: 0.95 }}
          >
            {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
          </motion.button>
        </div>
      </div>

      {/* New Password */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          New Password
        </label>
        <div className="relative">
          <input
            name="newPassword"
            type={showPasswords.new ? "text" : "password"}
            value={formData.newPassword}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-300 shadow-sm hover:shadow-md focus:shadow-lg"
          />
          <motion.button
            type="button"
            onClick={() => togglePassword("new")}
            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            whileTap={{ scale: 0.95 }}
          >
            {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
          </motion.button>
        </div>

        {/* Password Strength Meter */}
        {formData.newPassword && (
          <motion.div
            className="mt-2 space-y-1"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex gap-1 h-1.5">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  className={`flex-1 rounded-full ${
                    i <= passwordStrength
                      ? strengthColors[passwordStrength]
                      : "bg-gray-200"
                  }`}
                  initial={{ scaleX: 0 }}
                  animate={{
                    scaleX: 1,
                    transition: {
                      duration: 0.5,
                      delay: i * 0.1,
                    },
                  }}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500">
              Strength:
              <span
                className={`ml-1 font-medium ${strengthColors[
                  passwordStrength
                ].replace("bg-", "text-")}`}
              >
                {strengthLabels[passwordStrength]}
              </span>
            </p>
          </motion.div>
        )}
      </div>

      {/* Repeat Password */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Repeat Password
        </label>
        <div className="relative">
          <input
            name="repeatPassword"
            type={showPasswords.repeat ? "text" : "password"}
            value={formData.repeatPassword}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-300 shadow-sm hover:shadow-md focus:shadow-lg"
          />
          <motion.button
            type="button"
            onClick={() => togglePassword("repeat")}
            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            whileTap={{ scale: 0.95 }}
          >
            {showPasswords.repeat ? <EyeOff size={18} /> : <Eye size={18} />}
          </motion.button>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <motion.button
          type="submit"
          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium px-6 py-3 rounded-lg shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl flex items-center gap-2"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Changing...
            </>
          ) : (
            "Change Password"
          )}
        </motion.button>
      </div>

      <AnimatePresence>
        {changeStatus && (
          <motion.div
            className={`mt-4 p-3 rounded-lg border ${
              changeStatus.type === "success"
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            <p
              className={`text-sm flex items-center gap-2 ${
                changeStatus.type === "success"
                  ? "text-green-700"
                  : "text-red-700"
              }`}
            >
              {changeStatus.type === "success" ? (
                <CheckCircle size={18} className="flex-shrink-0" />
              ) : (
                <AlertCircle size={18} className="flex-shrink-0" />
              )}
              {changeStatus.message}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.form>
  );
};

export default ChangePasswordForm;
